/* External */
import {
  memo,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import { OrderedMap } from 'immutable';

/* Components */
import MiniEpgFilter from './MiniEpgFilter';
import Carousel from '../../../Carousel';

/* Utils */
import useGetPositionCarousel from '../../../../hooks/useGetPositionCarousel';
import { getRem, noOp } from '../../../../utils/functions';
import { COMPONENTS } from '../../../../utils/focusKey';
import { Direction } from '../../../../utils/spatialNavigation';

/* Styles */
import './MiniEpgFilter.scss';

function MiniEpgFilterList({
  filters,
  selectedFilterIndex,
  selectedFilterId,
  onFilterSelect,
  hideMiniEpg,
}) {
  const handleFilterBecameFocused = useCallback((_, { filterId }) => {
    onFilterSelect(filterId);
  }, [onFilterSelect]);

  const handleFilterEnterPress = useCallback(({ filterId }) => {
    onFilterSelect(filterId);
  }, [onFilterSelect]);

  const [positionCarousel, handleArrowPress, setPositionCarousel] = useGetPositionCarousel({
    carouselLength: filters?.size,
  });

  const handleArrowPressFilters = useCallback((direction) => {
    handleArrowPress(direction);
    if (direction === Direction.LEFT && positionCarousel === 0) {
      hideMiniEpg();
    }
  }, [handleArrowPress, hideMiniEpg, positionCarousel]);

  useEffect(() => {
    if (selectedFilterIndex >= 0) {
      setPositionCarousel(selectedFilterIndex);
    }
  }, [selectedFilterIndex, setPositionCarousel]);

  const filterComponents = useMemo(() => (
    filters?.valueSeq().map((filter) => (
      <MiniEpgFilter
        key={filter.id}
        focusKey={`${COMPONENTS.MINI_EPG_FILTER_LIST}${filter.id}`}
        filterId={filter.id}
        isSelected={filter.id === selectedFilterId}
        onBecameFocused={handleFilterBecameFocused}
        onEnterPress={handleFilterEnterPress}
        onArrowPress={handleArrowPressFilters}
      >
        {filter.name}
      </MiniEpgFilter>
    ))
  ), [
    filters,
    selectedFilterId,
    handleFilterBecameFocused,
    handleFilterEnterPress,
    handleArrowPressFilters,
  ]);

  return (
    <Carousel
      className="dtv-html5-c-mini-epg-filter-list"
      maxCardHeightRem={getRem(42)}
      positionCarousel={positionCarousel}
      setPositionCarousel={setPositionCarousel}
      centeredPosition
      roundedArrows
      withGradients
    >
      {filterComponents}
    </Carousel>
  );
}

MiniEpgFilterList.propTypes = {
  filters: PropTypes.instanceOf(OrderedMap),
  selectedFilterId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  selectedFilterIndex: PropTypes.number,
  onFilterSelect: PropTypes.func,
  hideMiniEpg: PropTypes.func,
};

MiniEpgFilterList.defaultProps = {
  filters: undefined,
  selectedFilterId: '',
  selectedFilterIndex: 0,
  onFilterSelect: noOp,
  hideMiniEpg: noOp,
};

export default memo(MiniEpgFilterList);
